<template>
  <!-- Mobile Only - Slide-up Modal -->
  <div class="md:hidden">
    <!-- Backdrop Overlay -->
    <transition name="backdrop-fade">
      <div
        v-if="showModal"
        class="fixed inset-0 bg-[#2F3033]/30 backdrop-blur-sm z-[60] transition-all duration-300"
        @click="closeModal"
      ></div>
    </transition>

    <!-- Slide-up Modal -->
    <transition name="slide-up">
      <div
        v-if="showModal"
        class="flex flex-col fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-xl border border-gray-200 z-[70] overflow-hidden max-h-[85vh]"
        style="bottom: calc(58px + env(safe-area-inset-bottom)); padding-top: env(safe-area-inset-top);"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="flex items-center justify-between px-6 py-6 sticky top-[env(safe-area-inset-top)] bg-white z-50">
          <h2 class="text-xl font-medium">Trip Finder</h2>
          <button @click="closeModal" class=" hover:text-gray-600 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <!-- Modal Body -->
        <div class="p-4 pb-0 overflow-y-auto flex-1">
          <div class=" leading-relaxed">
        
          </div>
          <!-- I want to - Mobile Toggle Switch (Horizontal) -->
          <TripFinderModeControl
            class="mb-6 w-full"
            :model-value="selectedIWant"
            variant="mobile"
            @update:modelValue="selectIWantOption"
          />

          <!-- Go - Mobile Toggle Switch (Horizontal) -->
          <TripFinderScopeControl
            v-if="selectedIWant && selectedIWant !== 'build'"
            class="mb-6 w-full"
            :local-label="detectedCountryName || 'Local'"
            :model-value="selectedGo"
            variant="mobile"
            @update:modelValue="selectGoOption"
          />
  
          <!-- WhereWhen -->
          <!-- WhereWhen -->
          <div v-if="selectedIWant">
            <!-- Location Component - Only show in Build a Trip mode -->
            <TripFinderBuildModeFields
              v-if="selectedIWant === 'build'"
              :date-display-text="dateHeaderText"
              :selected-date="selectedDate"
              :selected-go="selectedGo"
              :show-date-picker="showDatePicker"
              :temp-date="tempDate"
              variant="mobile"
              @date-selected="handleDateSelected"
              @location-selected="handleLocationSelected"
              @temp-date-changed="handleTempDateChanged"
              @toggle-date-picker="openDatePicker"
            />
          </div>

          <!-- Date selection (Always visible - Mobile accordion) -->

          <!-- RefineTrip -->
          <TripFinderMobileRefinePanel
            :disabled="!isRefineEnabled"
            :open="showAdventure"
            :mode="selectedIWant"
            :scope="selectedGo"
            @update:open="showAdventure = $event"
            @search="handleSearch"
          />

        </div>
        
        <!-- Search Footer -->
        <TripFinderMobileSearchFooter
          :is-search-enabled="isSearchEnabled"
          @search="handleSearch"
        />
      </div>
    </transition>
  </div>

  <!-- Desktop Only - Dropdown Modal -->
  <div class="hidden md:block">
    <!-- Backdrop Overlay for Desktop -->
    <transition name="backdrop-fade">
      <div
        v-if="showModal"
        class="fixed inset-0 z-40"
        :class="'bg-transparent'"
        @click="closeModal"
      ></div>
    </transition>

    <!-- Desktop Dropdown Modal -->
    <transition name="fade-slide-down">
      <div
        v-if="showModal"
        class="hidden md:block fixed top-[56px] left-0 right-0 w-full bg-white shadow-md border-b border-gray-200 z-50"
        @click.stop
      >
        <!-- Close Button -->
        <button 
          @click="closeModal" 
          class="absolute top-1/2 -translate-y-1/2 right-6 z-[60] cursor-pointer text-[#2F3033] hover:text-black transition-colors bg-white rounded-full p-1 hover:bg-gray-100 shadow-sm"
          aria-label="Close Trip Finder"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        
        <!-- Modal Body -->
        <div class="px-4 py-4 overflow-y-auto" style="max-height: 80vh;">
          <!-- Single Row Layout for Desktop -->
          <div class="flex relative z-50">
            <div class="tripfinder-desktop-glass w-full">
              <!-- Outer flex row: centered controls + right-pinned Refine/Search -->
              <div class="flex items-center justify-center gap-[10px]">

                  <!-- I want to - Desktop Toggle Switch -->
                  <TripFinderModeControl
                    class="flex-shrink-0"
                    :model-value="selectedIWant"
                    variant="desktop"
                    @update:modelValue="selectIWantOption"
                  />

                  <!-- Go - Desktop Toggle Switch (Discover mode) -->
                  <template v-if="selectedIWant && selectedIWant !== 'build'">
                    <TripFinderScopeControl
                      class="flex-shrink-0"
                      :local-label="detectedCountryName || 'Local'"
                      :model-value="selectedGo"
                      variant="desktop"
                      @update:modelValue="selectGoOption"
                    />
                  </template>

                  <!-- Location + Date (Plan Trip mode only) -->
                  <template v-if="selectedIWant === 'build'">
                    <TripFinderBuildModeFields
                      :date-display-text="desktopDateDisplayText"
                      :selected-date="selectedDate"
                      :selected-go="selectedGo"
                      :show-date-picker="showDatePicker"
                      :temp-date="tempDate"
                      variant="desktop"
                      @date-selected="handleDateSelected"
                      @location-selected="handleLocationSelected"
                      @temp-date-changed="handleTempDateChanged"
                      @toggle-date-picker="openDatePicker"
                    />
                  </template>

                <!-- Refine Trip + Search -->
                <div ref="refineButtonRef">
                  <TripFinderDesktopActionControls
                    :current-filter-count="currentFilterCount"
                    :is-refine-enabled="isRefineEnabled"
                    :is-refine-open="isRefineOpen"
                    :is-search-enabled="isSearchEnabled"
                    :is-searching="isSearching"
                    @search="handleSearch"
                    @toggle-refine="toggleDesktopRefine"
                  />
                </div>

              </div>
            </div>
          </div>
        </div>


        <TripFinderDesktopRefinePanel
          :open="isRefineOpen"
          :dropdown-style="refineDropdownStyle"
          :mode="selectedIWant"
          :scope="selectedGo"
          @update:open="isRefineOpen = $event"
          @search="handleSearch"
          @update:filter-count="handleFilterCountUpdate"
        />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import TripFinderDesktopRefinePanel from './TripFinderDesktopRefinePanel.vue'
import TripFinderDesktopActionControls from './TripFinderDesktopActionControls.vue'
import TripFinderBuildModeFields from './TripFinderBuildModeFields.vue'
import TripFinderMobileRefinePanel from './TripFinderMobileRefinePanel.vue'
import TripFinderMobileSearchFooter from './TripFinderMobileSearchFooter.vue'
import TripFinderModeControl from './TripFinderModeControl.vue'
import TripFinderScopeControl from './TripFinderScopeControl.vue'
import { useTripFinderActions } from '@/composables/useTripFinderActions.js'
import { useDetectedCountry } from '@/composables/useDetectedCountry.js'
import { useTripFinderForm } from '@/composables/useTripFinderForm.js'
import { useTripFinderShellBehavior } from '@/composables/useTripFinderShellBehavior.js'

// Props
const props = defineProps({
  showModal: {
    type: Boolean,
    default: false
  },
  isSearching: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'search'])

const {
  buildSearchPayload,
  dateHeaderText,
  desktopDateDisplayText,
  handleDateSelected,
  handleTempDateChanged,
  isRefineEnabled,
  isSearchEnabled,
  selectedDate,
  selectedDestination,
  selectedGo,
  selectedIWant,
  showDatePicker,
  tempDate,
  toggleDatePicker: openDatePicker,
  selectGoOption: applyGoOption,
  selectIWantOption: applyIWantOption,
  setSelectedDestination,
} = useTripFinderForm()

const { detectedCountryName } = useDetectedCountry({
  selectedDestination,
})

const {
  closeMobileRefinePanels,
  currentFilterCount,
  handleFilterCountUpdate,
  isRefineOpen,
  refineButtonRef,
  refineDropdownStyle,
  showAdventure,
  toggleDesktopRefine,
} = useTripFinderShellBehavior({
  showModal: computed(() => props.showModal),
  isSearchEnabled,
})

const {
  closeModal,
  handleGoOptionSelected: selectGoOption,
  handleIWantOptionSelected: selectIWantOption,
  handleLocationSelected,
  handleSearch,
} = useTripFinderActions({
  emit,
  buildSearchPayload,
  closeMobileRefinePanels,
  isSearchEnabled,
  selectGoOption: applyGoOption,
  selectIWantOption: applyIWantOption,
  setSelectedDestination,
})
</script>

<style scoped>
/* Slide-up Modal Animations */
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-active {
  transition: transform 250ms ease-out, opacity 250ms ease-out;
}

.slide-up-leave-active {
  transition: transform 200ms ease-in, opacity 200ms ease-in;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* Backdrop Fade Animations */
.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

.backdrop-fade-enter-active {
  transition: opacity 250ms ease-out;
}

.backdrop-fade-leave-active {
  transition: opacity 200ms ease-in;
}

.backdrop-fade-enter-to,
.backdrop-fade-leave-from {
  opacity: 1;
}

/* Desktop Fade + Slide-down Animations */
.fade-slide-down-enter-from,
.fade-slide-down-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

.fade-slide-down-enter-active {
  transition: transform 250ms ease-out, opacity 250ms ease-out;
}

.fade-slide-down-leave-active {
  transition: transform 200ms ease-in, opacity 200ms ease-in;
}

.fade-slide-down-enter-to,
.fade-slide-down-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* Basic modal styles */
button:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease-in-out;
}

button:active {
  transform: translateY(0);
}

/* Focus styles for accessibility */
button:focus {
  outline: none;
  outline-offset: 2px;
}

/* Custom scrollbar for mobile modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Calendar day hover effects */
.aspect-square:hover:not(:disabled) {
  transform: scale(1.05);
}

/* Smooth transitions for accordions */
.transition-transform {
  transition: transform 0.2s ease-in-out;
}

/* Mobile input styling to prevent zoom on iOS */
input[type="date"],
input[type="text"],
input[type="number"] {
  font-size: 16px;
  -webkit-appearance: none;
  appearance: none;
}

/* Desktop dropdown positioning */
.relative .absolute:not(.tripfinder-toggle-slider) {
  z-index: 50;
}

/* Higher z-index for When and Travellers dropdowns to overlay preferences */
.relative .absolute.when-dropdown,
.relative .absolute.travellers-dropdown {
  z-index: 60;
}

/* Ensure proper spacing for desktop layout */
@media (min-width: 768px) {
  .flex-wrap {
    flex-wrap: wrap;
  }
  
  .flex-1 {
    min-width: 200px;
  }
}

/* Mobile-specific adjustments */
@media (max-width: 767px) {
  .fixed.inset-0 {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
  
  .rounded-t-3xl {
    border-top-left-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
  }
}

/* Toggle switch animations */
.relative.inline-flex span {
  transition: transform 0.3s ease-in-out;
}

/* Staggered animation for adventure sections */
.stagger-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.stagger-enter-active {
  transition: all 0.4s ease-out;
}

.stagger-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.stagger-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.stagger-leave-active {
  transition: all 0.3s ease-in;
}

.stagger-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Staggered delay for each section */
.stagger-enter-active:nth-child(1) { transition-delay: 0ms; }
.stagger-enter-active:nth-child(2) { transition-delay: 100ms; }
.stagger-enter-active:nth-child(3) { transition-delay: 200ms; }
.stagger-enter-active:nth-child(4) { transition-delay: 300ms; }
.stagger-enter-active:nth-child(5) { transition-delay: 400ms; }
.stagger-enter-active:nth-child(6) { transition-delay: 500ms; }



/* Accordion content animations */
.space-y-4 > div {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Custom styling for counter buttons */
.w-8.h-8 {
  min-width: 2rem;
  min-height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ensure proper text alignment in counters */
.w-8.text-center {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
}

/* Shadow effects for desktop dropdowns */
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Responsive grid adjustments */
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

/* Ensure proper spacing in mobile layout */
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-3 > * + * {
  margin-top: 0.75rem;
}

/* Custom focus ring for toggle switches *//*
.focus\:ring-2:focus {
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
}
*/
/* Ensure proper color transitions */
/*.transition-colors {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}*/

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Prevent text selection on buttons */
button {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Ensure proper backdrop blur for modal */
.bg-black.bg-opacity-50 {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
</style>
